import { Popover } from '@base-ui/react';
import { tz, TZDate } from '@date-fns/tz';
import { Calendar } from '@components/calendar/Calendar';
import { Icon } from '@components/icon/Icon';
import {
  todayInTimezone,
  toISODateFormat,
  toShortWeekDateFormat,
} from '@shared/utils/dateFormatters';
import cx from 'classnames';
import { isValid, parseISO } from 'date-fns';
import { useRef, useState } from 'react';
import {
  type Control,
  type FieldPath,
  type FieldValues,
  useController,
} from 'react-hook-form';
import typography from '~styles/typography.scss';
import { ValidationErrorMessage } from '@components/formInputs/ValidationErrorMessage';
import { useRestaurant } from 'restaurantAdmin/context/useRestaurant';
import styles from './CouponDrawer.scss';

interface CouponExpirationDateFieldProps<
  T extends FieldValues = FieldValues,
  Name extends FieldPath<T> = FieldPath<T>,
> {
  control: Control<T>;
  name: Name;
}

export const CouponExpirationDateField = <
  T extends FieldValues = FieldValues,
  Name extends FieldPath<T> = FieldPath<T>,
>({
  control,
  name,
}: CouponExpirationDateFieldProps<T, Name>) => {
  const { timezone } = useRestaurant();
  const initialFocusRef = useRef(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [portalContainer, setPortalContainer] = useState<HTMLElement | null>(
    null,
  );

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
    if (open) {
      const dialog = containerRef.current?.closest('[role="dialog"]');
      setPortalContainer(dialog instanceof HTMLElement ? dialog : null);
    }
  };
  const {
    field: { onChange, value },
    fieldState: { error },
  } = useController({
    control,
    name,
    rules: {
      required: 'Expiration date is required',
      validate: (fieldValue: string) => {
        if (!fieldValue?.trim()) {
          return true;
        }
        const parsed = parseISO(fieldValue, { in: tz(timezone) });
        if (!isValid(parsed)) {
          return 'Enter a valid expiration date';
        }
        if (fieldValue < todayInTimezone(timezone)) {
          return 'Expiration date cannot be in the past';
        }
        return true;
      },
    },
  });

  const selectedDate = value
    ? parseISO(value, { in: tz(timezone) })
    : undefined;
  const hasSelectedDate = selectedDate != null && isValid(selectedDate);

  return (
    <div className={styles.expirationField} ref={containerRef}>
      <label className={cx(typography.c2_20, styles.drawerFieldLabel)}>
        Expiration
      </label>
      <Popover.Root open={isOpen} onOpenChange={handleOpenChange}>
        <Popover.Trigger
          aria-label="Choose expiration date"
          className={cx(styles.expirationTrigger, typography.t1, {
            [styles.expirationTriggerError]: !!error,
          })}
          type="button"
        >
          <Icon className={styles.expirationIcon} name="calendar" />
          <span
            className={cx({
              [styles.expirationPlaceholder]: !hasSelectedDate,
            })}
          >
            {hasSelectedDate
              ? toShortWeekDateFormat(selectedDate)
              : 'Select a date'}
          </span>
        </Popover.Trigger>
        <Popover.Portal container={portalContainer ?? undefined}>
          <Popover.Positioner
            align="start"
            collisionBoundary={portalContainer ?? document.documentElement}
            positionMethod="fixed"
            side="bottom"
            sideOffset={8}
          >
            <Popover.Popup
              className={styles.expirationCalendarPopover}
              initialFocus={initialFocusRef}
            >
              <Calendar
                minDate={TZDate.tz(timezone)}
                value={hasSelectedDate ? selectedDate : TZDate.tz(timezone)}
                onDateChange={(date) => {
                  onChange(toISODateFormat(date));
                  setIsOpen(false);
                }}
              />
            </Popover.Popup>
          </Popover.Positioner>
        </Popover.Portal>
      </Popover.Root>
      <ValidationErrorMessage error={error} label="Expiration" name={name} />
    </div>
  );
};
